import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Box, Paper, TextField, Button, Typography } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import axios from "../../config/axios";

function Login({ setIsAuthenticated, setAdmin }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const adminData = localStorage.getItem("admin");
    if (token && adminData) {
      setIsAuthenticated(true);
      setAdmin(JSON.parse(adminData));
      navigate("/dashboard");
    }
  }, [navigate, setIsAuthenticated, setAdmin]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/;
    const emailRegex =
      /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

    if (!formData.email || !emailRegex.test(formData.email))
      newErrors.email = "Valid email is required";
    if (!formData.password || !passwordRegex.test(formData.password))
      newErrors.password =
        "Password must be minimum 8 characters long and should contain atleast one uppercase, one lowercase and one special character.";

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    try {
      setLoading(true);
      setFormErrors({});

      const response = await axios.post("/auth/login", formData);
      const { token, admin } = response.data;

      if (!admin) {
        throw new Error("Admin data not received");
      }

      // Store token and admin data
      localStorage.setItem("token", token);
      localStorage.setItem("admin", JSON.stringify(admin));

      // Update app state
      setIsAuthenticated(true);
      setAdmin(admin);

      // Redirect to dashboard
      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error("Signup error:", error);
      setErrorMessage(
        error.response?.data?.message ||
          error.message ||
          "Error creating account"
      );
      // Clear any partial data
      localStorage.removeItem("token");
      localStorage.removeItem("admin");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 400,
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: "16px",
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          align="center"
          sx={{
            fontWeight: "bold",
            background: "#26A69A",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Admin Login
        </Typography>

        {errorMessage && (
          <div
            style={{
              background: "#FF6F61",
              color: "#FFFFFF",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              animation: "slideIn 0.3s ease-in-out",
            }}
          >
            <ErrorIcon style={{ marginRight: "8px" }} />
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            margin="normal"
            error={!!formErrors.email}
            helperText={
              formErrors.email && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#FF6F61",
                  }}
                >
                  <ErrorIcon style={{ fontSize: "16px", marginRight: "4px" }} />
                  {formErrors.email}
                </span>
              )
            }
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px",
                background: "#F5F5F5",
                transition: "all 0.3s",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#26A69A",
                boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
              },
              "& .MuiInputLabel-root": { color: "#263238", fontWeight: 500 },
              "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
            }}
          />
          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            margin="normal"
            error={!!formErrors.password}
            helperText={
              formErrors.password && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#FF6F61",
                  }}
                >
                  <ErrorIcon style={{ fontSize: "16px", marginRight: "4px" }} />
                  {formErrors.password}
                </span>
              )
            }
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px",
                background: "#F5F5F5",
                transition: "all 0.3s",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#26A69A",
                boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
              },
              "& .MuiInputLabel-root": { color: "#263238", fontWeight: 500 },
              "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              background: "#26A69A",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              py: 1.5,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.01)",
                boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
                background: "#26A69A",
              },
              "&:disabled": {
                background: "grey.500",
                transform: "none",
              },
            }}
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </Button>
        </form>
        <Link
          to="/email-verify"
          style={{ textDecoration: "none", cursor: "pointer" }}
        >
          <Typography
            gutterBottom
            align="center"
            sx={{
              fontWeight: "bold",
              background: "#26A69A",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Forgot Password?
          </Typography>
        </Link>
      </Paper>
    </Box>
  );
}

export default Login;
